// Feedback service for storing and managing user feedback
// 
// REFACTOR NOTE: This service now uses a unified feedback API approach where:
// - All feedback goes through /api/v1/feedback/ endpoint
// - Report feedback uses related_entity_type='report' and related_entity_id=<report_id>
// - Data object feedback uses related_entity_type='data_object' and related_entity_id=<data_object_id>
// - General feedback uses related_entity_type=null and related_entity_id=null
// 
// The legacy /api/v1/feedback/reports endpoint is deprecated but maintained for backward compatibility.
//
import { httpClient, unwrapApiResult } from './httpClient';
import { authenticatedHttpClient } from './authenticatedHttpClient';
import { authManager } from './authManager';
import { apiPath } from '../config/env';

// API payload interfaces matching backend documentation

// Unified feedback payload for all feedback types (POST /api/v1/feedback/)
export interface FeedbackPayload {
    user_email: string;
    related_entity_type?: string | null;
    related_entity_id?: string | null;
    like_dislike: boolean;
    feedback: {
        comment: string;
        rating: number;
        category: string;
        features_liked?: string[];
        suggestions?: string;
        severity?: string;
        tags?: string[];
    };
}

// Legacy interface for backward compatibility (deprecated)
export interface BasicFeedbackPayload extends FeedbackPayload { }

// Legacy interface for backward compatibility (deprecated)
export interface ReportFeedbackPayload {
    user_email: string;
    report_id: string;
    like_dislike: boolean;
    feedback: {
        issue?: string;
        rating?: number;
        category?: string;
        severity?: string;
        suggestions?: string;
        tags?: string[];
    };
}

// Admin feedback filter parameters interface
export interface AdminFeedbackFilters {
    user_email?: string;
    related_entity_type?: string;
    like_dislike?: boolean;
    feedback_contains?: string;
    from_date?: string;
    to_date?: string;
    limit?: number;
    offset?: number;
    order_by?: string;
    order_direction?: 'asc' | 'desc';
}

// API Response interface
export interface FeedbackResponse {
    id: string;
    user_email: string;
    related_entity_type?: string | null;
    related_entity_id?: string | null;
    like_dislike: boolean;
    feedback: Record<string, any>;
    created_at: string;
    updated_at: string;
    report_id?: string;
}

// Internal form data interface for component state
export interface FeedbackFormData {
    rating: number;
    likeDislike: boolean | null;
    selectedReport?: {
        id: string;
        reportId?: string | null; // Actual report ID (null for current data sources)
        name?: string;
        timestamp?: string;
        dataObjectId?: string;
        reportType?: 'historical' | 'current';
    } | null;
    entityType: string;
    dataObjectId: string | null;
    dataObjectName: string | null;
    severity?: string;
    tags: string[];
    feedback: string;
}

class FeedbackService {
    /**
     * Fetch all feedback for admin panel with advanced filtering
     * Requires authentication and admin group membership
     */
    async getAdminFeedback(filters?: AdminFeedbackFilters): Promise<{ feedback: FeedbackResponse[], total_count: number }> {
        try {
            const queryParams = new URLSearchParams();

            // Add filter parameters
            if (filters?.user_email) {
                queryParams.append('user_email', filters.user_email);
            }
            if (filters?.related_entity_type) {
                queryParams.append('related_entity_type', filters.related_entity_type);
            }
            if (filters?.like_dislike !== undefined) {
                queryParams.append('like_dislike', filters.like_dislike.toString());
            }
            if (filters?.feedback_contains) {
                queryParams.append('feedback_contains', filters.feedback_contains);
            }
            if (filters?.from_date) {
                queryParams.append('from_date', filters.from_date);
            }
            if (filters?.to_date) {
                queryParams.append('to_date', filters.to_date);
            }
            if (filters?.limit) {
                queryParams.append('limit', filters.limit.toString());
            }
            if (filters?.offset) {
                queryParams.append('offset', filters.offset.toString());
            }
            if (filters?.order_by) {
                queryParams.append('order_by', filters.order_by);
            }
            if (filters?.order_direction) {
                queryParams.append('order_direction', filters.order_direction);
            }

            // Use authenticated HTTP client for admin endpoints
            const response = await authenticatedHttpClient.get<{ feedback: FeedbackResponse[], total_count: number }>(
                apiPath(`feedback/admin/all?${queryParams.toString()}`)
            );

            const result = unwrapApiResult(response);
            return {
                feedback: result.feedback || [],
                total_count: result.total_count || 0
            };
        } catch (error) {
            console.error('❌ Failed to fetch feedback:', error);
            throw new Error('Failed to fetch feedback data');
        }
    }

    /**
     * Submit feedback to backend API
     * Uses unified feedback endpoint with related_entity_id for reports
     */
    async submitFeedback(formData: FeedbackFormData, userEmail?: string): Promise<FeedbackResponse> {
        try {
            // Get user email from auth manager if not provided
            const email = userEmail || authManager.getUserEmail();

            // Validate required data
            if (!formData.feedback?.trim()) {
                throw new Error('Feedback content is required');
            }

            // likeDislike can be true, false, or null (for neutral ratings)
            // Only validate that we have a rating, not the likeDislike value
            if (!formData.rating || formData.rating < 1) {
                throw new Error('Please select a rating (1-5 stars)');
            }

            // Use unified feedback endpoint for all feedback types
            return await this.submitUnifiedFeedback(formData, email);
        } catch (error) {
            console.error('❌ FEEDBACK SERVICE ERROR:', error);
            throw new Error(
                error instanceof Error
                    ? error.message
                    : 'Failed to submit feedback. Please try again.'
            );
        }
    }

    /**
     * Submit unified feedback to /api/v1/feedback/
     * Handles both general and report-specific feedback using related_entity_id
     */
    private async submitUnifiedFeedback(formData: FeedbackFormData, userEmail: string): Promise<FeedbackResponse> {
        // Determine entity type and ID based on feedback context
        let relatedEntityType: string | null = null;
        let relatedEntityId: string | null = null;

        // Only send report entity if user explicitly selected a historical report with a valid reportId
        if (formData.selectedReport?.reportId) {
            relatedEntityType = 'report';
            relatedEntityId = formData.selectedReport.reportId;
        }
        // No fallback - if no historical report selected, send null for both fields

        const payload: FeedbackPayload = {
            user_email: userEmail,
            related_entity_type: relatedEntityType,
            related_entity_id: relatedEntityId,
            like_dislike: formData.likeDislike!,
            feedback: {
                comment: formData.feedback,
                rating: formData.rating,
                category: formData.selectedReport?.reportId ? 'feedback_with_reports' : 'general',
                severity: formData.severity || 'medium',
                tags: formData.tags,
                suggestions: formData.feedback // Include the main feedback as suggestions too
            }
        };

        try {
            const result = await httpClient.post<FeedbackResponse>(
                apiPath('feedback/'),
                payload
            );

            return unwrapApiResult(result);
        } catch (error: any) {
            console.error('❌ Unified feedback API error:', error);
            throw error;
        }
    }

    /**
     * Submit general/basic feedback to /api/v1/feedback/
     * @deprecated Use submitUnifiedFeedback instead
     */
    private async submitBasicFeedback(formData: FeedbackFormData, userEmail: string): Promise<FeedbackResponse> {
        const payload: FeedbackPayload = {
            user_email: userEmail,
            related_entity_type: formData.entityType !== 'general' ? formData.entityType : null,
            related_entity_id: formData.dataObjectId || null,
            like_dislike: formData.likeDislike!,
            feedback: {
                comment: formData.feedback,
                rating: formData.rating,
                category: formData.selectedReport ? 'feedback_with_reports' : 'general',
                severity: formData.severity || 'medium',
                tags: formData.tags,
                suggestions: formData.feedback // Include the main feedback as suggestions too
            }
        };



        try {
            const result = await httpClient.post<FeedbackResponse>(
                apiPath('feedback/'),
                payload
            );

            return unwrapApiResult(result);
        } catch (error: any) {
            console.error('❌ Basic feedback API error:', error);

            throw error;
        }
    }

    /**
     * Submit report-specific feedback to /api/v1/feedback/reports
     * @deprecated Use submitUnifiedFeedback instead - reports now use related_entity_id
     */
    private async submitReportSpecificFeedback(formData: FeedbackFormData, userEmail: string): Promise<FeedbackResponse> {
        if (!formData.selectedReport?.id) {
            throw new Error('Report ID is required for report-specific feedback');
        }

        // Convert to unified format
        const payload: FeedbackPayload = {
            user_email: userEmail,
            related_entity_type: 'report',
            related_entity_id: formData.selectedReport.id,
            like_dislike: formData.likeDislike!,
            feedback: {
                comment: formData.likeDislike === false ? formData.feedback : (formData.feedback || ''),
                rating: formData.rating,
                category: 'feedback_with_reports',
                severity: formData.severity || 'medium',
                suggestions: formData.likeDislike === true ? formData.feedback : undefined,
                tags: formData.tags
            }
        };

        try {
            // Use unified endpoint instead of report-specific endpoint
            const result = await httpClient.post<FeedbackResponse>(
                apiPath('feedback/'),
                payload
            );

            return unwrapApiResult(result);
        } catch (error: any) {
            console.error('❌ Report feedback API error:', error);
            throw error;
        }
    }

    /**
     * Fallback localStorage storage when backend API is not available
     */
    private saveToLocalStorage(payload: FeedbackPayload, type: 'basic' | 'report'): FeedbackResponse {
        const fallbackData = {
            id: `feedback_${Date.now()}`,
            user_email: payload.user_email,
            related_entity_type: payload.related_entity_type,
            related_entity_id: payload.related_entity_id,
            like_dislike: payload.like_dislike,
            feedback: payload.feedback,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            report_id: payload.related_entity_type === 'report' ? payload.related_entity_id : undefined,
            type
        };

        // Store in localStorage
        const existingFeedback = JSON.parse(localStorage.getItem('feedback_submissions') || '[]');
        existingFeedback.push(fallbackData);
        localStorage.setItem('feedback_submissions', JSON.stringify(existingFeedback));

        return fallbackData;
    }

    /**
     * Get all feedback for the authenticated user
     * GET /api/v1/feedback/?user_email={email}
     */
    async getAllFeedback(): Promise<FeedbackResponse[]> {
        try {
            const userEmail = authManager.getUserEmail();

            const result = await httpClient.get<FeedbackResponse[]>(
                apiPath(`feedback?user_email=${encodeURIComponent(userEmail)}`)
            );

            return unwrapApiResult(result) || [];
        } catch (error) {
            console.error('Failed to fetch feedback:', error);
            throw new Error('Failed to fetch feedback. Please try again.');
        }
    }

    /**
     * Get feedback by ID
     * GET /api/v1/feedback/{id}
     */
    async getFeedbackById(feedbackId: string): Promise<FeedbackResponse> {
        try {
            const result = await httpClient.get<FeedbackResponse>(
                apiPath(`feedback/${feedbackId}`)
            );

            return unwrapApiResult(result);
        } catch (error) {
            console.error('Failed to fetch feedback by ID:', error);
            throw new Error('Failed to fetch feedback. Please try again.');
        }
    }

    /**
     * Update existing feedback
     * PUT /api/v1/feedback/{id}
     */
    async updateFeedback(feedbackId: string, formData: FeedbackFormData): Promise<FeedbackResponse> {
        try {
            const userEmail = authManager.getUserEmail();

            // Determine entity type and ID based on feedback context
            let relatedEntityType: string | null = null;
            let relatedEntityId: string | null = null;

            if (formData.selectedReport?.id) {
                // Report-specific feedback: use report as related entity
                relatedEntityType = 'report';
                relatedEntityId = formData.selectedReport.id;
            } else if (formData.dataObjectId) {
                // Data object feedback
                relatedEntityType = formData.entityType !== 'general' ? formData.entityType : 'data_object';
                relatedEntityId = formData.dataObjectId;
            } else if (formData.entityType !== 'general') {
                // Other entity types
                relatedEntityType = formData.entityType;
                relatedEntityId = formData.dataObjectId;
            }

            // Prepare update payload with unified structure
            const payload: Partial<FeedbackPayload> = {
                related_entity_type: relatedEntityType,
                related_entity_id: relatedEntityId,
                like_dislike: formData.likeDislike!,
                feedback: {
                    comment: formData.feedback,
                    rating: formData.rating,
                    category: formData.selectedReport ? 'feedback_with_reports' : 'general',
                    severity: formData.severity || 'medium',
                    tags: formData.tags,
                    suggestions: formData.feedback
                }
            };

            const result = await httpClient.put<FeedbackResponse>(
                apiPath(`feedback/${feedbackId}`),
                payload
            );

            return unwrapApiResult(result);
        } catch (error) {
            console.error('Failed to update feedback:', error);
            throw new Error('Failed to update feedback. Please try again.');
        }
    }

    /**
     * Delete feedback by ID
     * DELETE /api/v1/feedback/{id}
     */
    async deleteFeedback(feedbackId: string): Promise<void> {
        try {
            const result = await httpClient.delete(apiPath(`feedback/${feedbackId}`));
            unwrapApiResult(result); // Unwrap but don't return anything for void
        } catch (error) {
            console.error('Failed to delete feedback:', error);
            throw new Error('Failed to delete feedback. Please try again.');
        }
    }

    /**
     * Get feedback statistics (computed from API data)
     */
    async getFeedbackStats(): Promise<{
        totalCount: number;
        averageRating: number;
        ratingDistribution: Record<number, number>;
        commonTags: Array<{ tag: string; count: number }>;
        likeDislikeRatio: { likes: number; dislikes: number };
    }> {
        try {
            const feedback = await this.getAllFeedback();

            if (feedback.length === 0) {
                return {
                    totalCount: 0,
                    averageRating: 0,
                    ratingDistribution: {},
                    commonTags: [],
                    likeDislikeRatio: { likes: 0, dislikes: 0 }
                };
            }

            // Calculate statistics
            const ratingsWithValue = feedback.filter(f =>
                f.feedback.rating && typeof f.feedback.rating === 'number'
            );

            const totalRating = ratingsWithValue.reduce((sum, f) => sum + (f.feedback.rating || 0), 0);
            const averageRating = ratingsWithValue.length > 0 ? totalRating / ratingsWithValue.length : 0;

            // Rating distribution
            const ratingDistribution = ratingsWithValue.reduce((dist, f) => {
                const rating = f.feedback.rating || 0;
                dist[rating] = (dist[rating] || 0) + 1;
                return dist;
            }, {} as Record<number, number>);

            // Common tags
            const tagCounts = feedback.reduce((counts, f) => {
                const tags = f.feedback.tags || [];
                if (Array.isArray(tags)) {
                    tags.forEach(tag => {
                        counts[tag] = (counts[tag] || 0) + 1;
                    });
                }
                return counts;
            }, {} as Record<string, number>);

            const commonTags = Object.entries(tagCounts)
                .map(([tag, count]) => ({ tag, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10);

            // Like/dislike ratio
            const likes = feedback.filter(f => f.like_dislike === true).length;
            const dislikes = feedback.filter(f => f.like_dislike === false).length;

            return {
                totalCount: feedback.length,
                averageRating: Math.round(averageRating * 100) / 100,
                ratingDistribution,
                commonTags,
                likeDislikeRatio: { likes, dislikes }
            };
        } catch (error) {
            console.error('Failed to get feedback statistics:', error);
            throw new Error('Failed to get feedback statistics. Please try again.');
        }
    }
}

// Export singleton instance
export const feedbackService = new FeedbackService();
export default feedbackService;